import { api } from '@/utils/api';
import { getAllInfrastructuresByOrganizationRequest, Infrastructure } from './Infrastructure';
import { ParameterSource } from '../ParameterSource/ParameterSource';
import { Types } from './Types';
import { Parameter } from './Parameter';
import { sanitizePathParam } from '@/utils/sanitize';

export default class InfrastructureRepository {
	async getInfrastructureWithParams(infraestructureId: string): Promise<Infrastructure> {
		const iid = sanitizePathParam(infraestructureId);
		const data = await api.get(
			`/measurements/infrastructures/${iid}/parameters/all/sources/all`,
		);
		const infra = data.data;
		return {
			id: infra.id,
			name: infra.name,
			description: infra.description,
			inpId: infra.inpId,
			inpObjectId: infra.inpObjectId,
			inpObjectType: infra.inpObjectType,
			inUse: infra.inUse,
			latitude: infra.latitude,
			longitude: infra.longitude,
			locationId: infra.locationId,
			locationName: infra.locationName,
			infraTypeId: infra.infraTypeId,
			parameters: infra.parametersDTOList.map(
				(parameter: {
					inpId: number;
					inpObjectId: string;
					inpObjectType: number;
					parameterType: string;
					parameterTypeId: number;
					parameterUnit: string;
					parameterUnitId: number;
					id: number;
					name: string;
					expirationTime: number;
					forecastedRetentionTime: number;
					criticalHigh: number;
					criticalHhigh: number;
					criticalLow: number;
					criticalLlow: number;
					principal: boolean;
					retentionPolicy: number;
					sampleRate: number;
					historicalRetentionTime: number;
					txRate: number;
					visible: boolean;
					sourcesDTOList: any[];
					parameterForecastUnitId: number | null;
					parameterForecastTypeId: number | null;
					parameterForecastUnitName: string | null;
					parameterForecastUnitScale: number | null;
					parameterForecastUnitOffset: number | null;
					parameterForecastUnitIsCustom: boolean | null;
				}) => {
					return {
						id: parameter.id,
						name: parameter.name,
						expirationTime: parameter.expirationTime,
						forecastedRetentionTime: parameter.forecastedRetentionTime,
						highCritical: parameter.criticalHigh,
						hHighCritical: parameter.criticalHhigh,
						lowCritical: parameter.criticalLow,
						lLowCritical: parameter.criticalLlow,
						historicalPolicy: parameter.historicalRetentionTime,
						principal: parameter.principal,
						isVisible: parameter.visible,
						retentionPolicy: parameter.retentionPolicy,
						sampleRate: parameter.sampleRate,
						transferRate: parameter.txRate,
						parameterType: parameter.parameterType,
						paramTypeId: parameter.parameterTypeId,
						parameterUnit: parameter.parameterUnit,
						paramUnitId: parameter.parameterUnitId,
						inpId: parameter.inpId,
						inpObjectId: parameter.inpObjectId,
						inpObjectType: parameter.inpObjectType,
						parameterForecastUnitId: parameter.parameterForecastUnitId ?? null,
						parameterForecastTypeId: parameter.parameterForecastTypeId ?? null,
						parameterForecastUnitName: parameter.parameterForecastUnitName ?? null,
						parameterForecastUnitScale: parameter.parameterForecastUnitScale ?? null,
						parameterForecastUnitOffset: parameter.parameterForecastUnitOffset ?? null,
						parameterForecastUnitIsCustom: parameter.parameterForecastUnitIsCustom ?? null,
						sources: parameter.sourcesDTOList.map((source) => {
							const variablesDTO = JSON.parse(source.variablesDTOS || '[]');
							let virtualAtrributes = {};
							if (source.virtual) {
								if (source.virtualType === 'M') {
									virtualAtrributes = {
										virtualType: 'MODEL',
										inpId: source.inpId,
										objectModelId: source.inpObjectId,
										objectTypeId: source.inpObjectType,
									};
								} else {
									virtualAtrributes = {
										virtualType: 'CODE',
										statement: source.statement,
										variablesDTOS: variablesDTO.map((variable) => {
											return {
												name: variable.name,
												parameterId: variable.parameterId,
												parameterName: variable.parameterString,
											};
										}),
										virtualCurrentTs: source.virtualCurrentTs,
									};
								}
							}
							const [serialDeviceId, serialSensorId] = (source.sourceSerialNumber ?? '').split(':');
							return {
								id: source.id,
								name: source.name,
								isEnabled: source.enabled,
								isVirtual: source.virtual,
								sourceSerialNumber: source.sourceSerialNumber,
								forecastMetService: source.forecastMetService,
								historicalMetService: source.historicalMetService,
								unitId: source.unitId,
								unitName: source.unitName,
								unitOffset: source.unitOffset,
								unitScale: source.unitScale,
								unitIsCustom: source.unitIsCustom,
								deviceId: source.deviceId ?? (serialDeviceId || null),
								sensorId: source.sensorId ?? (serialSensorId || null),
								deviceLatitude: source.deviceLatitude,
								deviceLongitude: source.deviceLongitude,
								sensorLatitude: source.sensorLatitude,
								sensorLongitude: source.sensorLongitude,
								...virtualAtrributes,
							} as ParameterSource;
						}),
					} as any;
				},
			),
		} as Infrastructure;
	}

	async getInfrastructureTypes(): Promise<Types[]> {
		const types = await api.get(`measurements/infrastructure/types`);
		return types.data.map((type: { infrastructureTypeId: any; infrastructureTypeName: any }) => {
			return {
				value: type.infrastructureTypeId,
				label: type.infrastructureTypeName,
			};
		});
	}

	async postAddParameter(infrastructureId: string, parameters: Parameter): Promise<any> {
		const iid = sanitizePathParam(infrastructureId);
		const parameter = await api.post(
			`measurements/infrastructures/${iid}/parameters`,
			parameters,
		);
		return parameter.data;
	}

	async getInfraestructureInUse(
		location: string | number,
		page: number,
		size: number,
		name: string | null,
		infraId: any | null,
		infraTypeId: number | string | null,
		showInDashboard: boolean | null,
	): Promise<any> {
		if (!location) return [];

		const params: any = {
			page,
			size,
		};

		if (infraId) {
			params.infraId = infraId;
		}

		if (name && name !== '') {
			params.name = name;
		}

		if (infraTypeId && infraTypeId !== 0) {
			params.infraTypeIds = infraTypeId; // Send as array
		}

		if (showInDashboard !== null) {
			params.showInDashboard = showInDashboard;
		}

		const parameters = await api.get(`/measurements/location/${location}/inuse`, { params });
		return parameters.data;
	}

	async getMeasurementTarget(infrastructureId: string): Promise<any> {
		const iid = sanitizePathParam(infrastructureId);
		const all = await api.get(
			`measurements/infraestructure/${iid}/measurementTarget/all`,
		);
		return all.data;
	}

	async putInfraestructureDescription(
		infrastructureId: string | number,
		description: string,
		infrastructureName: string,
	): Promise<any> {
		const iid = sanitizePathParam(infrastructureId);
		const response = await api.put(`measurements/infrastructures/${iid}/description`, {
			description,
			infrastructureName,
		});
		return response.data;
	}

	async getAllInfrastructuresByOrganization({
		organizationId,
		page,
		size,
	}: getAllInfrastructuresByOrganizationRequest) {
		const orgId = sanitizePathParam(organizationId);
		const response = await api.get(`measurements/infrastructures/organizations/${orgId}`, {
			params: { page, size },
		});
		return response.data;
	}

	async postFavoriteGroup(locationId: number | string, group: any): Promise<any> {
		const locId = sanitizePathParam(locationId);
		const response = await api.post(`measurements/locations/${locId}/favorite-groups`, group);
		return response.data;
	}

	async getFavoriteGroups(locationId: number | string): Promise<any> {
		const locId = sanitizePathParam(locationId);
		const response = await api.get(`measurements/locations/${locId}/favorite-groups`);
		return response.data;
	}

	async deleteFavoriteGroup(locationId: number | string, groupId: number): Promise<any> {
		const locId = sanitizePathParam(locationId);
		const gid = sanitizePathParam(groupId);
		const response = await api.delete(`measurements/locations/${locId}/favorite-groups/${gid}`);
		return response.data;
	}
}
